/**
 * ModeStandingsWidget (web) — Annie Mode Phase 8 (PR #152).
 *
 * Mirrors apps/mobile/src/components/ModeStandingsWidget.tsx. Period toggle,
 * skeleton on cold load, render-gating on `data.period === period` to prevent
 * stale-period display.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { modePalettes } from '@trading-app/shared';
import { tradingService } from '../services/trading';
import { useAuth } from '../contexts/AuthContext';

type Period = 'today' | 'week' | 'month' | 'all';
const PERIODS: Array<{ key: Period; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all', label: 'All-time' },
];

export function ModeStandingsWidget() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<Period>('all');

  const query = useQuery({
    queryKey: ['mode-standings', period],
    queryFn: () => tradingService.getModeStandings(period),
    enabled: !!user,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });

  // Gate render on period match to prevent stale-period P&L display.
  const data = query.data && query.data.period === period ? query.data : null;
  const isInitialLoad = !data;

  return (
    <div className="rounded-2xl border border-border-gray bg-white p-4 mb-4">
      <div className="text-text-gray text-xs uppercase tracking-wider mb-3">Mode Standings</div>
      <div className="flex gap-2 mb-4">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-opacity active:opacity-70 ${period === p.key ? 'bg-brand-primary text-white' : 'border border-border-gray text-type-primary'}`}
            aria-pressed={period === p.key}
          >
            {p.label}
          </button>
        ))}
      </div>

      {isInitialLoad ? (
        <div className="flex items-stretch gap-3">
          <div className="flex-1 h-20 rounded-xl bg-border-gray/40" />
          <div className="w-8" />
          <div className="flex-1 h-20 rounded-xl bg-border-gray/40" />
        </div>
      ) : (
        <ModeStandingsBody eric={data!.eric} annie={data!.annie} leader={data!.leader} />
      )}
    </div>
  );
}

function ModeStandingsBody({
  eric,
  annie,
  leader,
}: {
  eric: { netPnl: number; tradeCount: number; wins: number; avgPnl: number };
  annie: { netPnl: number; tradeCount: number; wins: number; avgPnl: number };
  leader: 'ERIC' | 'ANNIE' | 'TIE';
}) {
  const lead = Math.abs(eric.netPnl - annie.netPnl);
  return (
    <div>
      <div className="flex items-stretch gap-3">
        <ModeCard palette={modePalettes.ERIC} row={eric} highlighted={leader === 'ERIC'} />
        <div className="flex flex-col items-center justify-center">
          <span className="text-text-gray text-xs uppercase">vs</span>
          {leader !== 'TIE' && (
            <span className="text-text-gray text-xs mt-1">+${lead.toFixed(0)}</span>
          )}
        </div>
        <ModeCard palette={modePalettes.ANNIE} row={annie} highlighted={leader === 'ANNIE'} />
      </div>
    </div>
  );
}

function ModeCard({
  palette,
  row,
  highlighted,
}: {
  palette: { name: string; brandPrimary: string };
  row: { netPnl: number; tradeCount: number };
  highlighted: boolean;
}) {
  const pnlClass = row.netPnl > 0 ? 'text-rh-green' : row.netPnl < 0 ? 'text-rh-red' : 'text-text-gray';
  return (
    <div
      className={`flex-1 rounded-xl p-3 ${highlighted ? '' : 'opacity-70'}`}
      style={{
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: highlighted ? palette.brandPrimary : '#d9d9d9',
      }}
    >
      <div className="text-xs font-semibold mb-1" style={{ color: palette.brandPrimary }}>
        {palette.name}
      </div>
      <div className={`text-xl font-semibold ${pnlClass}`}>
        {row.netPnl >= 0 ? '+' : ''}${row.netPnl.toFixed(2)}
      </div>
      <div className="text-text-gray text-xs mt-1">
        {row.tradeCount} trade{row.tradeCount === 1 ? '' : 's'}
      </div>
    </div>
  );
}
